/**
 * One-field edits of an action, applied optimistically.
 *
 * Inline cells need a write that feels instant and a read that stays whole, and
 * the action endpoints give neither for free: the PUT is full-body with no
 * copy-forward, and the list responses carry join output the write path must
 * not see. `actionEditBody` handles the second problem; this hook handles the
 * first, writing the new value into the react-query cache before the request
 * goes out and rolling the snapshot back if it fails.
 *
 * It deliberately does not reuse
 * `useUpdateIncidentAction`/`useUpdateOrgAction`: those do their own post-hoc
 * cache surgery (incident) or plain invalidation (org), which would fight the
 * optimistic entry and make the cell flicker.
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from 'constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { IapScope, INCIDENT } from '../ICSConstants';
import { actionEditBody } from '../actionsTable/actionEditModel';
import { useActualIncidentId } from './commonHooks';
import { keys as incidentKeys } from './incidentActionHooks';
import { keys as orgKeys } from './orgActionHooks';
import type { ActionEntity } from './useScopedActionForm';
import { actionErrorMessage } from './actionErrors';
import {
  isHeleneDemoActionId,
  updateHeleneDemoOrgAction,
} from '../cfa-demo/heleneDemoOrgActions';
import { orgEventKeys } from 'hooks/orgEvents/orgEventHooks';

const browserUtcOffset = () => -new Date().getTimezoneOffset();

export type ActionFieldPatch = Record<string, unknown>;

type EditVariables = {
  action: ActionEntity;
  patch: ActionFieldPatch;
};

type EditContext = {
  /** Every matching cache entry, so a rollback restores exactly what was there. */
  snapshots: [readonly unknown[], unknown][];
};

/**
 * Cache entries holding this scope's action list.
 *
 * The org key carries a trailing CFA feature-flag segment, so both sides are
 * matched by prefix rather than by an exact key.
 */
function useActionListKey(scope: IapScope, incidentIdOverride?: string | null) {
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);

  return {
    incidentId,
    groupGuid,
    userGuid,
    queryKey:
      scope === INCIDENT
        ? incidentKeys.byIncidentAndUser(incidentId, userGuid)
        : orgKeys.byGroupAndUser(groupGuid, userGuid),
  };
}

export const useActionFieldEdit = (
  scope: IapScope,
  incidentIdOverride?: string | null
) => {
  const isIncident = scope === INCIDENT;
  const queryClient = useQueryClient();
  const { incidentId, groupGuid, userGuid, queryKey } = useActionListKey(
    scope,
    incidentIdOverride
  );

  const mutation = useMutation<unknown, unknown, EditVariables, EditContext>({
    mutationFn: async ({ action, patch }) => {
      const body = actionEditBody(
        action as unknown as Record<string, unknown>,
        patch
      );

      if (isIncident) {
        return axios.put(
          `${config.apiGateway.ics_incident_actions}/${incidentId}/${action.id}`,
          { ...body, archived: body.archived ?? false, user_guid: userGuid }
        );
      }

      if (isHeleneDemoActionId(action.action_id)) {
        updateHeleneDemoOrgAction(body as never);
        return undefined;
      }

      return axios.put(
        `${config.apiGateway.ics_org_actions}/${groupGuid}/${action.action_id}`,
        {
          ...body,
          archived: body.archived ?? false,
          utc_offset: browserUtcOffset(),
          user_guid: userGuid,
        }
      );
    },

    onMutate: async ({ action, patch }) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData({ queryKey });

      queryClient.setQueriesData<ActionEntity[]>({ queryKey }, (rows) => {
        // Prefix match also hits the rollup query, which is an object.
        if (!Array.isArray(rows)) return rows;
        return rows.map((row) =>
          row.action_id === action.action_id
            ? ({ ...row, ...patch } as ActionEntity)
            : row
        );
      });

      return { snapshots: snapshots as [readonly unknown[], unknown][] };
    },

    onError: (error, _variables, context) => {
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      toast.error(actionErrorMessage(error));
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      if (!isIncident) {
        void queryClient.invalidateQueries({ queryKey: orgEventKeys.all });
      }
    },
  });

  /**
   * The cells await this, so a rejection has to stay caught here: the
   * optimistic entry is already rolled back and the toast already shown, and an
   * unhandled rejection out of a popover's onValueChange takes the page down
   * with it.
   */
  const editAction = useCallback(
    async (action: ActionEntity, patch: ActionFieldPatch) => {
      try {
        await mutation.mutateAsync({ action, patch });
        return true;
      } catch {
        return false;
      }
    },
    [mutation]
  );

  return { editAction, isEditing: mutation.isPending };
};

export type ActionFieldEditor = ReturnType<typeof useActionFieldEdit>;
